import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  CardBody,
  Row,
  Col,
  Spinner,
  FormGroup,
  Input,
  Alert,
} from "reactstrap";
import toast from "react-hot-toast";
import UserService from "../../../services/OrderingServices/ProfileServices";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import StatusBadge from "../../../Components/Common/Badge";

const FeatureSettings = () => {
  const [organizationData, setOrganizationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // Individual loading states for each toggle
  const [kotbotUpdating, setKotbotUpdating] = useState(false);
  const [sectionUpdating, setSectionUpdating] = useState(false);
  const [takeAwayUpdating, setTakeAwayUpdating] = useState(false);

  // Toggle states
  const [usesKotBot, setUsesKotBot] = useState(false);
  const [usesSection, setUsesSection] = useState(false);
  const [usesTakeAway, setUsesTakeAway] = useState(false);

  const fetchOrganizationData = async () => {
    try {
      setLoading(true);
      const data = await UserService.getList();
      setOrganizationData(data);

      setUsesKotBot(data.organizationInfo?.usesKotBotService || false);
      setUsesTakeAway(data.organizationInfo?.enableTakeAway || false);
      setUsesSection(data.organizationInfo?.shouldShowSections || false);

      setFetchError(null);
    } catch (err) {
      console.error("Error fetching organization data:", err);
      setFetchError("Failed to load settings. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizationData();
  }, []);

  const handleKotBotToggle = async (checked) => {
    try {
      setKotbotUpdating(true);
      await UserService.updateKotBot(organizationData.id, checked);
      setUsesKotBot(checked);
      toast.success(checked ? "KOT Bot Enabled" : "KOT Bot Disabled");
    } catch (err) {
      toast.error("Failed to update KOT Bot");
    } finally {
      setKotbotUpdating(false);
    }
  };

  const handleSectionToggle = async (checked) => {
    try {
      setSectionUpdating(true);
      await UserService.updateSection(organizationData.id, checked);
      setUsesSection(checked);

      // Update localStorage as you were doing before
      const storedUser = JSON.parse(localStorage.getItem("user")) || {};
      storedUser.shouldShowSections = checked;
      localStorage.setItem("user", JSON.stringify(storedUser));

      toast.success(checked ? "Sections Enabled" : "Sections Disabled");
    } catch (err) {
      toast.error("Failed to update Sections");
    } finally {
      setSectionUpdating(false);
    }
  };

  const handleTakeAwayToggle = async (checked) => {
    try {
      setTakeAwayUpdating(true);
      await UserService.updateTakeAway(organizationData.id, checked);
      setUsesTakeAway(checked);
      toast.success(checked ? "Take Away Enabled" : "Take Away Disabled");
    } catch (err) {
      toast.error("Failed to update Take Away");
    } finally {
      setTakeAwayUpdating(false);
    }
  };

  const features = [
    {
      id: "kotbot",
      title: "KOT Bot Service",
      description: "Enable automated KOT printing via bot service",
      icon: "🖶",
      checked: usesKotBot,
      updating: kotbotUpdating,
      handler: handleKotBotToggle,
      color: "primary",
    },
    {
      id: "sections",
      title: "Show Sections",
      description: "Toggle visibility of sections in the POS interface",
      icon: "📋",
      checked: usesSection,
      updating: sectionUpdating,
      handler: handleSectionToggle,
      color: "success",
    },
    {
      id: "takeaway",
      title: "Take Away Orders",
      description: "Allow customers to place take-away orders",
      icon: "🛍️",
      checked: usesTakeAway,
      updating: takeAwayUpdating,
      handler: handleTakeAwayToggle,
      color: "info",
    },
  ];

  return (
    <div
      className="page-content"
      style={{
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
      }}
    >
      <Container fluid>
        <BreadCrumb title="Feature Settings" pageTitle="System Setup" />
        <div className="">
          <h3 className="fw-bold" style={{ color: "#2c3e50" }}>
            Feature Controls
          </h3>
          {/* <p className="text-muted mb-0">
            Manage your restaurant's features and services
          </p> */}
        </div>

        {fetchError && (
          <Alert color="danger" className="shadow-sm border-0 mb-4">
            <i className="mdi mdi-alert-circle me-2"></i>
            {fetchError}
          </Alert>
        )}

        {loading ? (
          <Card className="shadow-sm border-0">
            <CardBody className="text-center py-5">
              <Spinner
                color="primary"
                style={{ width: "3rem", height: "3rem" }}
              />
              <p className="mt-4 text-muted fw-medium">
                Loading feature settings...
              </p>
            </CardBody>
          </Card>
        ) : (
          <Row>
            {features.map((feature) => (
              <Col lg="4" md="6" className="" key={feature.id}>
                <Card
                  className="border-0 shadow-sm"
                  style={{
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    transform: feature.checked ? "translateY(-2px)" : "none",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-5px)";
                    e.currentTarget.style.boxShadow =
                      "0 0.5rem 1.5rem rgba(0,0,0,0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = feature.checked
                      ? "translateY(-2px)"
                      : "none";
                    e.currentTarget.style.boxShadow = "";
                  }}
                >
                  <CardBody className="p-3">
                    <div className="d-flex align-items-start justify-content-between mb-2">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle"
                        style={{
                          width: "48px",
                          height: "48px",
                          backgroundColor: feature.checked
                            ? `rgba(var(--bs-${feature.color}-rgb), 0.1)`
                            : "#f1f3f5",
                          fontSize: "24px",
                          transition: "all 0.3s ease",
                        }}
                      >
                        {feature.icon}
                      </div>

                      <FormGroup switch className="">
                        <Input
                          type="switch"
                          checked={feature.checked}
                          disabled={feature.updating}
                          onChange={(e) => feature.handler(e.target.checked)}
                          style={{
                            width: "48px",
                            height: "24px",
                            cursor: feature.updating
                              ? "not-allowed"
                              : "pointer",
                          }}
                        />
                      </FormGroup>
                    </div>

                    <h5
                      className="mb-2 fw-semibold justify-content-between d-flex align-items-center"
                      style={{ color: "#2c3e50" }}
                    >
                      {feature.title}

                      <StatusBadge
                        status={feature.checked}
                        type="status"
                        pill={true}
                      />
                    </h5>

                    <p className="text-muted" style={{ fontSize: "0.9rem" }}>
                      {feature.description}
                    </p>
                    {/* 
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <StatusBadge
                          status={feature.checked}
                          type="status"
                          pill={true}
                        />
                      </div>

                      {feature.updating && (
                        <div className="d-flex align-items-center text-muted">
                          <Spinner size="sm" className="me-2" />
                          <small>Updating...</small>
                        </div>
                      )}
                    </div> */}
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        <Row className="">
          <Col>
            <Card
              className="border-0 shadow-sm"
              style={{ backgroundColor: "#fff8e1" }}
            >
              <CardBody className="p-4">
                <div className="d-flex align-items-start">
                  <div className="me-3" style={{ fontSize: "24px" }}>
                    💡
                  </div>
                  <div>
                    <h6
                      className="mb-2 fw-semibold"
                      style={{ color: "#f57c00" }}
                    >
                      Notice
                    </h6>
                    <p
                      className="mb-0 text-muted"
                      style={{ fontSize: "0.9rem" }}
                    >
                      Changes to these features will take effect after you log
                      out and log back in. Toggle them on or off based on your
                      current operational needs
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default FeatureSettings;
